import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * DesktopFrame — the shell that wraps every route.
 *
 * Mobile (<1024px): completely transparent.
 *
 * Desktop (lg ≥1024px):
 *   • Reserves left padding for <DesktopSidebar /> (260–280px).
 *   • Reserves right padding for <DesktopRightRail /> (320px) on xl+ EXCEPT
 *     on chat workspace routes — there the conversation pane already owns
 *     the right side and the rail is hidden.
 *   • Pages self-constrain to phone-width via their own `max-w-md mx-auto`.
 *
 * Sidebar + Right rail are `position: fixed`, so the wrapper only reserves
 * viewport space via padding — it does not contain them.
 */

// Routes where the right rail is hidden (chat surface owns the right side).
const NO_RIGHT_RAIL = [
  /^\/chats(\/.*)?$/,
  /^\/groups\/[^/]+$/,
  /^\/friends\/[^/]+$/,
];

export default function DesktopFrame({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const showSidebar = !!user;
  const showRightRail = showSidebar && !NO_RIGHT_RAIL.some((rx) => rx.test(location.pathname));

  return (
    <div className="relative min-h-screen w-full bg-[#16171A]">
      {/* Ambient gradient washes — gentle, only visible on desktop. */}
      <div className="hidden lg:block fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-32 left-1/3 w-[36rem] h-[36rem] rounded-full bg-[#B95F73]/8 blur-[140px]" />
        <div className="absolute -bottom-40 right-1/4 w-[40rem] h-[40rem] rounded-full bg-[#C9A961]/8 blur-[160px]" />
      </div>

      <div
        className={[
          "relative z-10",
          showSidebar ? "lg:pl-[260px] xl:pl-[280px]" : "",
          showRightRail ? "xl:pr-[300px]" : "",
        ].join(" ")}
      >
        {children}
      </div>
    </div>
  );
}
